import React from 'react';

// =============================================================================
// CARD RENDERING FUNCTIONS
// =============================================================================

/**
 * Unified card rendering function with configurable sizes
 * Supports multiple card sizes: normal, small, mobile, mobileLarge, showdownSmall, showdownNormal
 */
export const renderCard = (card: string | null, size: string = 'normal', index?: number) => {
  if (!card) return null;
  
  const isRed = card.includes('♥') || card.includes('♦');
  const suit = card.slice(-1); // Get the suit symbol
  const rank = card.slice(0, -1); // Get everything except the suit
  const colorClass = isRed ? 'text-red-600' : 'text-black';
  
  // Define size configurations
  const sizeConfigs = {
    normal: {
      container: 'inline-block bg-white rounded-lg shadow-md border-2 border-gray-300 px-3 py-4 mx-1 min-w-[60px] text-center',
      rankClass: 'font-bold text-lg leading-tight',
      suitClass: 'text-2xl leading-none'
    },
    small: {
      container: null, // Small cards are rendered as fragments
      rankClass: 'font-bold text-xs',
      suitClass: 'text-sm'
    },
    mobile: {
      container: 'w-12 h-16 bg-white rounded border flex flex-col items-center justify-center text-xs',
      rankClass: 'font-bold text-xs',
      suitClass: 'text-sm'
    },
    mobileLarge: {
      container: 'w-16 h-20 bg-white rounded border flex flex-col items-center justify-center',
      rankClass: 'font-bold text-sm',
      suitClass: 'text-lg'
    },
    showdownSmall: {
      container: 'w-6 h-9 bg-white rounded border flex flex-col items-center justify-center text-xs',
      rankClass: 'font-bold text-xs',
      suitClass: 'text-xs'
    },
    showdownNormal: {
      container: 'w-12 h-16 bg-white rounded-lg shadow-lg flex flex-col items-center justify-center border-2 border-gray-300',
      rankClass: 'font-bold text-sm',
      suitClass: 'text-base'
    }
  };
  
  const config = sizeConfigs[size as keyof typeof sizeConfigs] || sizeConfigs.normal;
  
  const cardContent = (
    <>
      <div className={`${config.rankClass} ${colorClass}`}>
        {rank}
      </div>
      <div className={`${config.suitClass} ${colorClass}`}>
        {suit}
      </div>
    </>
  );
  
  // For small cards, return fragment (no container)
  if (size === 'small') {
    return cardContent;
  }
  
  // For all other sizes, wrap in container
  return (
    <div key={index} className={config.container}>
      {cardContent}
    </div>
  );
};

// =============================================================================
// PLAYER STATUS HELPERS
// =============================================================================

/**
 * Get player status indicator classes and text
 */
export const getPlayerStatusInfo = (player: any, gameState: any, myPlayerId: string, timeoutWarnings: Set<string>) => {
  const isCurrentTurn = gameState?.currentPlayerTurn === player.id;
  const isFolded = player.isFolded;
  const isConnected = player.isConnected !== false; // Default to true if not specified
  const isSittingOut = player.sittingOut;
  
  const containerClasses = player.id === myPlayerId 
    ? 'bg-blue-900/50 border-blue-600' 
    : isCurrentTurn
    ? 'bg-green-900/30 border-green-500'
    : isFolded
    ? 'bg-gray-800/50 border-gray-600 opacity-60'
    : !isConnected
    ? 'bg-red-900/30 border-red-600 opacity-75'
    : 'bg-gray-700 border-gray-600';

  const avatarClasses = !isConnected ? 'bg-red-600' :
    isCurrentTurn ? 'bg-green-600 animate-pulse' : 'bg-gradient-to-r from-blue-500 to-purple-600';

  const statusClasses = !isConnected ? 'bg-red-500' :
    isFolded ? 'bg-red-500' : 
    isSittingOut ? 'bg-yellow-500' :
    'bg-green-500';

  const nameClasses = timeoutWarnings.has(player.id) ? 'timeout-warning' : 'text-white';

  return {
    containerClasses,
    avatarClasses,
    statusClasses,
    nameClasses,
    isCurrentTurn,
    isFolded,
    isConnected,
    isSittingOut
  };
};

// =============================================================================
// GAME STATISTICS AND FORMATTING
// =============================================================================

/**
 * Format chips amount with proper currency display
 */
export const formatChips = (chips: number): string => {
  return `$${chips || 0}`;
};

/**
 * Get betting round display name
 */
export const getBettingRoundName = (round: string): string => {
  const roundNames = {
    'preflop': 'Pre-flop',
    'flop': 'Flop',
    'turn': 'Turn',
    'river': 'River',
    'showdown': 'Showdown'
  };
  return roundNames[round as keyof typeof roundNames] || round;
};

/**
 * Calculate player score for leaderboard
 */
export const calculatePlayerScore = (player: any): number => {
  return player.chips - (player.totalBuyIns || (1000 * (1 + (player.rebuyCount || 0))));
};

/**
 * Sort players for leaderboard display
 */
export const sortPlayersForLeaderboard = (players: any[]): any[] => {
  return players
    .map(p => ({
      ...p,
      calculatedScore: calculatePlayerScore(p)
    }))
    .sort((a, b) => b.calculatedScore - a.calculatedScore);
};

/**
 * Get leaderboard position styling
 */
export const getLeaderboardPositionStyle = (index: number): string => {
  return index === 0 ? 'bg-yellow-500' : 
         index === 1 ? 'bg-gray-400' : 
         index === 2 ? 'bg-orange-500' : 'bg-gray-600';
};

// =============================================================================
// MOBILE UI COMPONENTS
// =============================================================================

/**
 * Mobile header component
 */
export const MobileHeader = ({ 
  onBackClick, 
  title, 
  pot 
}: { 
  onBackClick: () => void; 
  title: string; 
  pot?: number;
}) => (
  <div className="bg-gray-800 p-4 flex items-center justify-between">
    <button
      onClick={onBackClick}
      className="px-3 py-1 bg-gray-700 text-white rounded text-sm"
    >
      ← Players
    </button>
    <h1 className="text-lg font-bold text-white">{title}</h1>
    <div className="text-white text-sm">
      {pot !== undefined && `$${pot}`}
    </div>
  </div>
);

/**
 * Mobile view switcher component
 */
export const MobileViewSwitcher = ({ 
  currentView, 
  onViewChange, 
  onLeaderboardClick 
}: {
  currentView: string;
  onViewChange: (view: string) => void;
  onLeaderboardClick: () => void;
}) => (
  <div className="bg-gray-800 border-t border-gray-700 px-4 py-2">
    <div className="flex bg-gray-700 rounded-lg">
      <button
        onClick={() => onViewChange('table')}
        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
          currentView === 'table' 
            ? 'bg-blue-600 text-white' 
            : 'text-gray-400 hover:text-white'
        }`}
      >
        Table
      </button>
      <button
        onClick={() => onViewChange('stats')}
        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
          currentView === 'stats' 
            ? 'bg-blue-600 text-white' 
            : 'text-gray-400 hover:text-white'
        }`}
      >
        Statistics
      </button>
      <button
        onClick={onLeaderboardClick}
        className="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors text-gray-400 hover:text-white"
      >
        🏆 Leaderboard
      </button>
    </div>
  </div>
);

/**
 * Mobile community cards display
 */
export const MobileCommunityCards = ({ 
  gameState 
}: { 
  gameState: any;
}) => (
  gameState?.communityCards && gameState.communityCards.length > 0 ? (
    <div className="bg-gray-800 rounded-lg p-4 mb-4">
      <h3 className="text-white font-semibold mb-2 text-center">Community Cards</h3>
      <div className="flex justify-center gap-2 flex-wrap">
        {gameState.communityCards.map((card: string, i: number) => 
          renderCard(card, 'mobile', i)
        )}
      </div>
    </div>
  ) : null
);

/**
 * Mobile player hole cards display
 */
export const MobileHoleCards = ({ 
  holeCards 
}: { 
  holeCards: string[];
}) => (
  <div className="bg-gray-800 rounded-lg p-4 mb-4">
    <h3 className="text-white font-semibold mb-2 text-center">Your Cards</h3>
    <div className="flex justify-center gap-2">
      {holeCards && holeCards.length > 0 ? (
        holeCards.map((card, i) => 
          renderCard(card, 'mobileLarge', i)
        )
      ) : (
        <div className="text-gray-400">No cards dealt</div>
      )}
    </div>
  </div>
);

// =============================================================================
// ACTION BUTTON COMPONENTS
// =============================================================================

/**
 * Action button component with consistent styling
 */
export const ActionButton = ({ 
  onClick, 
  disabled = false, 
  variant = 'primary',
  size = 'normal',
  children,
  className = ''
}: {
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  size?: 'normal' | 'large' | 'small';
  children: React.ReactNode;
  className?: string;
}) => {
  const baseClasses = 'font-semibold rounded transition-colors touch-manipulation';
  
  const variantClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white',
    secondary: 'bg-gray-600 hover:bg-gray-700 active:bg-gray-800 text-white',
    danger: 'bg-red-600 hover:bg-red-700 active:bg-red-800 text-white',
    success: 'bg-green-600 hover:bg-green-700 active:bg-green-800 text-white',
    warning: 'bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white'
  };
  
  const sizeClasses = {
    small: 'px-3 py-2 text-sm min-h-[40px]',
    normal: 'px-4 py-2 text-base min-h-[50px]',
    large: 'px-6 py-4 text-lg min-h-[60px]'
  };
  
  const disabledClasses = disabled 
    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
    : variantClasses[variant];
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${disabledClasses} ${sizeClasses[size]} ${className}`}
    >
      {children}
    </button>
  );
};

/**
 * Poker action buttons component
 */
export const PokerActionButtons = ({ 
  onAction,
  canCheck,
  callAmount,
  gameState,
  currentPlayer,
  p2pGameMode = false
}: {
  onAction: (action: string, amount?: number) => void;
  canCheck: boolean;
  callAmount: number;
  gameState: any;
  currentPlayer: any;
  p2pGameMode?: boolean;
}) => (
  <div className="space-y-3">
    <p className="text-green-400 font-bold text-center mb-3">🎯 Your Turn - Choose Action</p>
    <div className="flex flex-wrap gap-2 justify-center">
      <ActionButton
        onClick={() => onAction('fold')}
        variant="danger"
      >
        Fold
      </ActionButton>
      
      {canCheck ? (
        <ActionButton
          onClick={() => onAction('check')}
          variant="secondary"
        >
          {p2pGameMode ? '🔍 ZK-Check' : 'Check'}
        </ActionButton>
      ) : (
        <ActionButton
          onClick={() => onAction('call', callAmount)}
          variant="primary"
        >
          {p2pGameMode ? `🔐 ZK-Call $${callAmount}` : `Call $${callAmount}`}
        </ActionButton>
      )}
      
      <ActionButton
        onClick={() => onAction('raise', gameState.currentBet + gameState.minRaise)}
        variant="success"
      >
        Raise ${gameState.currentBet + gameState.minRaise}
      </ActionButton>
      
      <ActionButton
        onClick={() => onAction('all-in')}
        variant="warning"
        disabled={currentPlayer?.chips === 0}
      >
        All-In ${currentPlayer?.chips || 0}
      </ActionButton>
    </div>
  </div>
);

// =============================================================================
// NOTIFICATION SYSTEM
// =============================================================================

/**
 * Notification item component
 */
export const NotificationItem = ({ 
  notification, 
  onDismiss 
}: { 
  notification: { id: number; message: string; type: string; }; 
  onDismiss: (id: number) => void;
}) => {
  const typeClasses = {
    success: 'bg-green-600 border-green-500',
    error: 'bg-red-600 border-red-500',
    warning: 'bg-yellow-600 border-yellow-500',
    info: 'bg-blue-600 border-blue-500'
  };
  
  return (
    <div className={`${typeClasses[notification.type as keyof typeof typeClasses] || typeClasses.info} text-white px-4 py-2 rounded-lg shadow-lg border flex items-center justify-between`}>
      <span className="text-sm">{notification.message}</span>
      <button 
        onClick={() => onDismiss(notification.id)}
        className="ml-2 text-white hover:text-gray-200 font-bold"
      >
        ×
      </button>
    </div>
  );
};

/**
 * Notifications container component
 */
export const NotificationsContainer = ({ 
  notifications, 
  onDismiss 
}: { 
  notifications: any[]; 
  onDismiss: (id: number) => void;
}) => (
  <div className="fixed top-4 right-4 space-y-2 z-50 max-w-sm">
    {notifications.map((notification) => (
      <NotificationItem 
        key={notification.id} 
        notification={notification} 
        onDismiss={onDismiss} 
      />
    ))}
  </div>
);

// =============================================================================
// GAME INFO DISPLAY
// =============================================================================

/**
 * Game information display component
 */
export const GameInfoDisplay = ({ 
  gameState, 
  callAmount, 
  currentPlayer 
}: {
  gameState: any;
  callAmount: number;
  currentPlayer: any;
}) => (
  <div className="bg-gray-700 rounded-lg p-4 mb-4">
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div>
        <span className="text-gray-400">Pot:</span>
        <span className="text-green-400 font-bold ml-2">{formatChips(gameState.pot)}</span>
      </div>
      <div>
        <span className="text-gray-400">To Call:</span>
        <span className="text-yellow-400 font-bold ml-2">{formatChips(callAmount)}</span>
      </div>
      <div>
        <span className="text-gray-400">Your Chips:</span>
        <span className="text-blue-400 font-bold ml-2">{formatChips(currentPlayer?.chips || 0)}</span>
      </div>
      <div>
        <span className="text-gray-400">Your Bet:</span>
        <span className="text-purple-400 font-bold ml-2">{formatChips(currentPlayer?.currentBet || 0)}</span>
      </div>
    </div>
  </div>
);

// =============================================================================
// P2P STATUS DISPLAY
// =============================================================================

/**
 * P2P status indicator component
 */
export const P2PStatusDisplay = ({ 
  p2pStatus, 
  p2pGameMode, 
  connectionStatus 
}: {
  p2pStatus: any;
  p2pGameMode: boolean;
  connectionStatus: string;
}) => (
  <div className="text-right">
    <div className={`text-sm font-semibold ${connectionStatus === 'connected' ? 'text-green-400' : 'text-red-400'}`}>
      {connectionStatus === 'connected' ? '🔒 Connected' : 
       connectionStatus === 'connecting' ? '🔄 Connecting' : 
       '❌ Disconnected'}
    </div>
    {connectionStatus === 'connected' && (
      <div className="text-xs text-gray-400 space-y-1">
        <div className="flex items-center space-x-2">
          <span className={`w-2 h-2 rounded-full ${p2pStatus.websocket === 'connected' ? 'bg-green-500' : 'bg-gray-500'}`}></span>
          <span>WebSocket</span>
          <span className={`w-2 h-2 rounded-full ${p2pStatus.p2p === 'connected' ? 'bg-blue-500' : 'bg-gray-500'}`}></span>
          <span>P2P ({p2pStatus.connectedPeers})</span>
        </div>
        {p2pStatus.fallbackMode && (
          <div className="text-yellow-400 text-xs">⚠️ Fallback Mode</div>
        )}
        {p2pGameMode && (
          <div className="text-purple-400 text-xs">🔐 Mental Poker</div>
        )}
      </div>
    )}
  </div>
);

// =============================================================================
// EMPTY SLOT COMPONENTS
// =============================================================================

/**
 * Empty player slot component
 */
export const EmptyPlayerSlot = ({ index }: { index: number }) => (
  <div key={`empty-${index}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-700/50 border-2 border-dashed border-gray-600">
    <div className="flex items-center">
      <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center mr-3">
        <span className="text-gray-400 text-sm">?</span>
      </div>
      <p className="text-gray-500">Waiting for player...</p>
    </div>
  </div>
);

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if enough players with chips are available to start game
 */
export const getPlayersWithChips = (players: any[]): any[] => {
  return players.filter(p => (p.chips || 0) > 0 && (p.isConnected === true || p.isConnected === undefined));
};

/**
 * Get current player from players list
 */
export const getCurrentPlayer = (players: any[], myPlayerId: string): any | null => {
  return players.find(p => p.id === myPlayerId) || null;
};

/**
 * Check if player can check (no bet to call)
 */
export const canPlayerCheck = (currentPlayer: any, gameState: any): boolean => {
  if (!currentPlayer || !gameState) return false;
  return (currentPlayer.currentBet || 0) === gameState.currentBet;
};

/**
 * Calculate call amount for player
 */
export const calculateCallAmount = (currentPlayer: any, gameState: any): number => {
  if (!currentPlayer || !gameState) return 0;
  return Math.max(0, gameState.currentBet - (currentPlayer.currentBet || 0));
};